import {
  ClipboardEvent,
  KeyboardEvent,
  RefObject,
  useCallback,
  useRef,
  useState
} from "react"

interface IUseOtpReturn {
  otp: string[]
  otpValue: string
  otpRefs: RefObject<Array<HTMLInputElement | null>>
  handleChange: (index: number, value: string) => void
  handleKeyDown: (index: number, event: KeyboardEvent<HTMLInputElement>) => void
  handlePaste: (event: ClipboardEvent<HTMLInputElement>) => void
  reset: () => void
  focusFirst: () => void
}

export const useOtp = (length: number): IUseOtpReturn => {
  const [otp, setOtp] = useState<string[]>(() => Array(length).fill(""))
  const otpRefs = useRef<Array<HTMLInputElement | null>>([])

  const handleChange = useCallback(
    (index: number, value: string): void => {
      const char = value.slice(-1)
      if (char && !/^\d$/.test(char)) return

      setOtp((prev) => {
        const next = [...prev]
        next[index] = char
        return next
      })

      if (char && index < length - 1) {
        otpRefs.current[index + 1]?.focus()
      }
    },
    [length]
  )

  const handleKeyDown = useCallback(
    (index: number, event: KeyboardEvent<HTMLInputElement>): void => {
      if (event.key === "Backspace" && !otp[index] && index > 0) {
        otpRefs.current[index - 1]?.focus()
      }
    },
    [otp]
  )

  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLInputElement>): void => {
      event.preventDefault()
      const pasted = event.clipboardData
        .getData("text")
        .replace(/\D/g, "")
        .slice(0, length)
      if (!pasted) return

      setOtp((prev) => {
        const next = [...prev]
        for (let i = 0; i < pasted.length; i++) {
          next[i] = pasted[i]
        }
        return next
      })

      const focusIndex = Math.min(pasted.length, length - 1)
      otpRefs.current[focusIndex]?.focus()
    },
    [length]
  )

  const reset = useCallback((): void => {
    setOtp(Array(length).fill(""))
  }, [length])

  const focusFirst = useCallback((): void => {
    otpRefs.current[0]?.focus()
  }, [])

  return {
    otp,
    otpValue: otp.join(""),
    otpRefs,
    handleChange,
    handleKeyDown,
    handlePaste,
    reset,
    focusFirst
  }
}
